import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/articles";

const categoryNames: Record<string, string> = {
  "saishi-zixun": "赛事资讯",
  "shuju-fenxi": "数据分析",
  "xiazai-zhinan": "下载指南",
  "shiyong-jiaocheng": "使用教程",
  "hangye-dongtai": "行业动态",
  "yonghu-gushi": "用户故事",
};

export default function Article() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-secondary-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-secondary-500"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categoryNames[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.imageUrl,
    url: `${window.location.origin}/article/${article.id}`,
    publisher: {
      "@type": "Organization",
      name: "大满贯APP",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: window.location.origin,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: categoryName,
        item: `${window.location.origin}/category/${article.category}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed mb-6">
                {article.summary}
              </p>

              <div className="flex items-center gap-3 text-sm text-foreground-400">
                <span>作者：{article.author}</span>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 大满贯APP`}
                className="w-full h-auto object-cover max-h-[500px]"
              />
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:md:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                prose-strong:text-foreground-900
                [&_p]:text-base [&_p]:leading-relaxed [&_p]:mb-5 [&_p]:text-foreground-700
                [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-10 [&_h3]:mb-4
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="text-xl font-bold text-foreground-900 mb-8">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 flex-1">
                        {rel.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}